import React, { useState, useEffect, useRef } from 'react';
import { axiosInstance } from '../api/api';
import { hooks } from '../hooks';
import { components } from '../components';
import { actions } from '../store/actions';
import { Routes, TabScreens } from '../routes';
import logoCheck from '../assets/bg/check-list.png';
import logoPin from '../assets/bg/location.png';
import { CameraPreview, type CameraPreviewFlashMode, type CameraPreviewOptions } from '@capacitor-community/camera-preview';
import { Capacitor } from '@capacitor/core';

interface NFCData {
  code?: string;
  name?: string;
  serialNumber?: string;
}

interface LocationState {
  nfcData?: NFCData;
  spotName?: string;
  locationCode?: string;
}

const MESSAGES = {
  validation: {
    descriptionRequired: 'Patrol deskripsi harus diisi!',
    photosRequired: 'Minimal ambil 1 bukti gambar',
    fileTooLarge: 'file gambar terlalu besar, tolong ambil gambar dibawah 4Mb',
    fileTooLargeTitle: 'Ukuran file terlalu besar',
  },
  success: {
    submit: 'Laporan Patroli berhasil dikirim!',
    nfcScan: 'NFC Tag berhasil discan!',
  },
  labels: {
    uploadPhotos: 'Kirim foto bukti laporan*',
    patrolDescription: 'Deskripsi Patroli*',
    nfcInfo: 'Informasi NFC Tag',
    submitReport: 'Kirim laporan patroli',
    submitting: 'Mengirim laporanmu...',
  },
  placeholders: {
    description: 'Jelaskan kondisi lokasi patroli, apakah ada masalah, kendala atau hal janggal yang terjadi dan ditemukan selama proses patroli..',
  },
  tips: {
    photos: 'Tip: Ambil gambar yang jelas dan sesaui untuk laporan patroli',
    description: 'Tip: Tolong jelaskan dengan jelas laporan untuk patroli berikut.',
  },
  errors: {
    submit: 'Gagal untuk mengirim laporan patroli. Tolong coba lagi.',
    serverError: ' Server error. Tolong hubungi tim terkait jika masalah terus berlanjut.',
  }
};

const FileSizePopup: React.FC<{
  isVisible: boolean;
  onClose: () => void;
  title: string;
  message: string;
}> = ({ isVisible, onClose, title, message }) => {
  if (!isVisible) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      padding: 20
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: 16,
        padding: 24,
        maxWidth: 320,
        width: '100%',
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        textAlign: 'center'
      }}>
        <div style={{
          width: 60, height: 60, borderRadius: '50%',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 16px'
        }}>
          <span style={{ fontSize: 28 }}>🖼️</span>
        </div>
        <h3 style={{
          margin: '0 0 12px 0',
          fontSize: 18,
          fontWeight: 600,
          color: '#1a1a1a'
        }}>{title}</h3>
        <p style={{ margin: '0 0 24px 0', fontSize: 14, color: '#666', lineHeight: 1.5 }}>
          {message}
        </p>
        <button
          onClick={onClose}
          style={{
            width: '100%', padding: 14,
            backgroundColor: '#ef4444',
            color: 'white',
            border: 'none',
            borderRadius: 12,
            fontSize: 16,
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#dc2626';
            e.currentTarget.style.transform = 'translateY(-1px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#ef4444';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          Mengerti
        </button>
      </div>
    </div>
  );
};

const CameraComponent: React.FC<{
  open: boolean;
  onClose: () => void;
  onSave: (base64?: string) => void;
}> = ({ open, onClose, onSave }) => {
  const isNative = Capacitor.isNativePlatform();
  const [flashModes, setFlashModes] = useState<CameraPreviewFlashMode[]>([]);
  const [width, setWidth] = useState(window.innerWidth);
  const [height, setHeight] = useState(window.innerHeight);
  const [flashMode, setFlashMode] = useState<CameraPreviewFlashMode>('off');
  const [preview, setPreview] = useState<string>();
  const [cameraStarted, setCameraStarted] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout>();

  const [cameraPreviewOptions] = useState<CameraPreviewOptions>({
    parent: isNative ? 'camera-root' : 'cameraPreview',
    position: 'rear',
    toBack: false,
    y: Math.floor(window.innerHeight * 0.1),
    height: isNative ? Math.floor(window.innerHeight - (window.innerHeight * 0.1) - (window.innerHeight * 0.25)) : width,
    width: isNative ? width : height
  });

  useEffect(() => {
    (async () => {
      try {
        const fm = await CameraPreview.getSupportedFlashModes();
        setFlashModes(fm.result);
        if (fm.result.length > 0) {
          setFlashMode(fm.result[0]);
        }
      } catch (error) {
        
      }
    })();
  }, [open]);

  useEffect(() => {
    if (flashMode) {
      CameraPreview.setFlashMode({ flashMode });
    }
  }, [flashMode]);

  useEffect(() => {
    const startCamera = async () => {
      try {
        await CameraPreview.start(cameraPreviewOptions);
        setCameraStarted(true);
      } catch (error) {

      }
    };

    const stopCamera = async () => {
      try {
        await CameraPreview.stop();
        setCameraStarted(false);
        setPreview(undefined);
      } catch (error) {

      }
    };

    if (open) {
      timeoutRef.current = setTimeout(startCamera, 400);
    } else {
      stopCamera();
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (cameraStarted) {
        stopCamera();
      }
    };
  }, [open, cameraPreviewOptions, cameraStarted]);

  useEffect(() => {
    const handleResize = () => {
      setWidth(window.innerWidth);
      setHeight(window.innerHeight);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const takePicture = () => {
    CameraPreview.capture({ quality: 100, width, height }).then((image: { value: string }) => {
      const base64Image = `data:image/jpeg;base64,${image.value}`;
      setPreview(base64Image);
    }).catch((error: any) => {

    });
  };

  const toggleFlash = () => {
    if (flashModes.length > 0) {
      const index = flashModes.indexOf(flashMode);
      const nextIndex = index === flashModes.length - 1 ? 0 : index + 1;
      setFlashMode(flashModes[nextIndex]);
    }
  };

  const saveImage = () => {
    onSave(preview);
  };

  const retakePicture = () => {
    setPreview(undefined);
  };

  if (!open) return null;

  return (
    <div style={{ 
      position: 'fixed', 
      top: 0, 
      left: 0, 
      right: 0, 
      bottom: 0, 
      backgroundColor: 'black',
      zIndex: 1000 
    }}>
      <div id="cameraPreview" style={{ width: '100%', height: '100%', position: 'relative' }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '10px',
          zIndex: 10
        }}>
          {flashModes.length > 0 && (
            <button onClick={toggleFlash} style={{
              background: 'rgba(0,0,0,0.5)',
              color: 'white',
              border: 'none',
              padding: '8px 12px',
              borderRadius: '20px',
              fontSize: 14,
              cursor: 'pointer'
            }}>
              Flash: {flashMode || 'off'}
            </button>
          )}
          <button onClick={onClose} style={{
            background: 'rgba(0,0,0,0.5)',
            color: 'white',
            border: 'none',
            padding: '8px 12px',
            borderRadius: '50%',
            width: '40px',
            height: '40px',
            fontSize: 16,
            cursor: 'pointer'
          }}>
            ✕
          </button>
        </div>
        
        <div style={{
          position: 'absolute',
          bottom: '20px',
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '0 20px',
          zIndex: 10
        }}>
          {preview ? (
            <button onClick={retakePicture} style={{
              background: 'rgba(0,0,0,0.5)',
              border: 'none',
              borderRadius: '10px',
              width: '60px',
              height: '60px',
              overflow: 'hidden',
              cursor: 'pointer'
            }}>
              <div style={{ 
                backgroundImage: `url(${preview})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                width: '100%',
                height: '100%'
              }}></div>
            </button>
          ) : (
            <div style={{ width: '60px' }}></div>
          )}
          
          <button onClick={takePicture} style={{
            background: 'white',
            border: 'none',
            borderRadius: '50%',
            width: '70px',
            height: '70px',
            fontSize: '24px',
            fontWeight: 'bold',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            ●
          </button>
          
          {preview ? (
            <button onClick={saveImage} style={{
              background: 'rgba(0,0,0,0.5)',
              color: 'white',
              border: 'none',
              borderRadius: '50%',
              width: '50px',
              height: '50px',
              fontSize: '20px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              ✓
            </button>
          ) : (
            <div style={{ width: '60px' }}></div>
          )}
        </div>
      </div>
    </div>
  );
};

export const ClockInSuccess: React.FC = () => {
  const dispatch = hooks.useDispatch();
  const navigate = hooks.useNavigate();
  const articlesResult = hooks.useGetArticles();
  const location = hooks.useLocation();
  const { nfcData, spotName, locationCode } = (location?.state as LocationState) || {};

  const [opacity, setOpacity] = useState<number>(0);
  const [description, setDescription] = useState<string>('');
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [mounted, setMounted] = useState<boolean>(true);
  const [showFileSizePopup, setShowFileSizePopup] = useState<boolean>(false);
  const [cameraOpen, setCameraOpen] = useState<boolean>(false);
  
  const patrolGroupId = localStorage.getItem('patrol_group_id');

  useEffect(() => () => setMounted(false), []);

  hooks.useScrollToTop();
  hooks.useOpacity(setOpacity);
  hooks.useThemeColor('#F6F9F9', '#F6F9F9', dispatch);

  const dataURLtoFile = (dataurl: string, filename: string): File => {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)![1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    
    return new File([u8arr], filename, { type: mime });
  };

  const handleCameraSave = (base64?: string) => {
    if (base64) {
      const newFile = dataURLtoFile(base64, `patrol_evidence_${Date.now()}.jpg`);
      setFiles(prev => [...prev, newFile]);
    }
    setCameraOpen(false);
  };

  const handleOpenCamera = () => {
    setCameraOpen(true);
  };

  const handleRemovePhoto = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setDescription(e.target.value);
  };

  const handleSubmit = async () => {
    if (!description.trim()) { 
      alert(MESSAGES.validation.descriptionRequired); 
      return; 
    }
    if (files.length === 0) { 
      alert(MESSAGES.validation.photosRequired); 
      return; 
    }
    if (files.some(f => f.size > 4 * 1024 * 1024)) { 
      setShowFileSizePopup(true); 
      return; 
    }

    setSubmitting(true);

    try {
      const formData = new FormData();
      formData.append('patrol_group_id', patrolGroupId || '');
      formData.append('code', nfcData?.code || locationCode || '');
      formData.append('description', description.trim());
      files.forEach(f => formData.append('files[]', f));

      await axiosInstance.post('/api/v1/patrol/user/route/store', formData);

      if (mounted) {
        localStorage.setItem('last_scanned_location', spotName || nfcData?.name || nfcData?.code || 'Lokasi tidak ditemukan');
        const refreshEvent = new CustomEvent('refreshPatrolStatus');
        window.dispatchEvent(refreshEvent);
        
        dispatch(actions.setScreen(TabScreens.Home));
        navigate(Routes.TabNavigator, { replace: true });
        alert(MESSAGES.success.submit);
      }
    } catch (error: any) {
      if (mounted) {
        const errorMessage = error.response?.data?.message || error.message || MESSAGES.errors.submit;
        alert(`Gagal Mengirim: ${errorMessage}`);
      }
    } finally {
      if (mounted) setSubmitting(false);
    }
  };

  const handleBack = () => {
    dispatch(actions.setScreen(TabScreens.Home));
    navigate(Routes.TabNavigator, { replace: true });
  };

  const renderHeader = () => (
    <components.Header title='Laporan Patroli' showGoBack={true} />
  );

  const renderNFCLocationInfo = () => (
    <div style={{marginBottom: 20, padding: 20, textAlign: 'center'}}>
      <h1 style={{marginBottom:10, marginTop:20, fontWeight:600, fontSize:24}}>
        {spotName || nfcData?.name || 'Lokasi tidak ditemukan'}
      </h1>
      <div style={{color:'#666', fontSize:13}}>
        <div style={{display:'flex', alignItems:'center', gap:8, justifyContent:'center', marginBottom:12}}>
          <img src={logoPin} alt="location" style={{width:18,height:18,opacity:0.8}} />
          <span>{nfcData?.name || spotName || 'Lokasi'}</span>
        </div>
        {nfcData?.code && <p><strong>Kode NFC:</strong> {nfcData.code}</p>}
        {nfcData?.serialNumber && <p><strong>Tag SN:</strong> {nfcData.serialNumber}</p>}
      </div>
    </div>
  );

  const renderSuccessIndicator = () => (
    <div style={{marginBottom:20,padding:'0 20px',display:'flex',justifyContent:'center',alignItems:'center',flexDirection:'column'}}>
      <div style={{
        width:160, height:160, borderRadius:'50%', position:'relative', backgroundColor:'rgba(34,197,94,0.1)',
        border:'15px solid #fff', display:'flex', justifyContent:'center', alignItems:'center', flexDirection:'column', boxShadow:'0 8px 25px rgba(0,0,0,0.1)'
      }}>
        <img src={logoCheck} alt="Check" style={{width:52,height:52,opacity:0.8}} />
        <h2 style={{marginTop:8,color:'#22c55e',fontSize:16,fontWeight:600}}><components.LiveClock /></h2>
      </div>
      <p style={{marginTop:20,textAlign:'center',color:'#4a5568',lineHeight:1.5,fontSize:14,maxWidth:280}}>
        {MESSAGES.success.nfcScan} <br /> Tolong selesaikan laporan patroli dibawah ini
      </p>
    </div>
  );

  const renderForm = () => (
    <section style={{padding:'0 20px'}}>
      <ul style={{display:'flex', flexDirection:'column', gap:24}}>
        <li>
          <div style={{marginBottom:12}}>
            <label>{MESSAGES.labels.uploadPhotos}</label>
          </div>
          
          <button 
            onClick={handleOpenCamera}
            style={{
              width: '100%',
              padding: 16,
              border: '2px dashed #e2e8f0',
              borderRadius: 12,
              backgroundColor: '#fafafa',
              cursor: 'pointer',
              fontSize: 16,
              color: '#4a5568',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 8,
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f0f0f0';
              e.currentTarget.style.borderColor = '#cbd5e0';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#fafafa';
              e.currentTarget.style.borderColor = '#e2e8f0';
            }}
          >
            <span style={{ fontSize: 24 }}>📷</span>
            <span>Ambil gambar dengan kamera</span>
            <small style={{ color: '#718096', fontSize: 12 }}>
              {files.length > 0 ? `${files.length} foto yang sudah diambil` : 'Ketuk untuk membuka kamera'}
            </small>
          </button>

          {files.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', 
                gap: 8,
                marginBottom: 12
              }}>
                {files.map((file, index) => (
                  <div key={index} style={{ position: 'relative' }}>
                    <img 
                      src={URL.createObjectURL(file)} 
                      alt={`Evidence ${index + 1}`}
                      style={{
                        width: '80px',
                        height: '80px',
                        borderRadius: '8px',
                        objectFit: 'cover',
                        border: '2px solid #e2e8f0'
                      }}
                    />
                    <button
                      onClick={() => handleRemovePhoto(index)}
                      style={{
                        position: 'absolute',
                        top: '-8px',
                        right: '-8px',
                        background: '#ef4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: '50%',
                        width: '20px',
                        height: '20px',
                        fontSize: '12px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        lineHeight: 1
                      }}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
              <div style={{ color: '#22c55e', fontSize: 13 }}>
                ✓ {files.length} bukti foto siap dikirimkan.
              </div>
            </div>
          )}

          <div style={{color:'#718096', fontSize:13, marginTop:8}}>
            <div>• Minimal 1 foto dibutuhkan sebagai bukti laporan</div>
            <div>• Foto diminta untuk diambil langsung mengguakan kamera</div>
            <div style={{color:'#4299e1',marginTop:4}}>{MESSAGES.tips.photos}</div>
          </div>
        </li>

        <li>
          <div style={{marginBottom:12}}>
            <label>{MESSAGES.labels.patrolDescription}</label>
          </div>
          <textarea 
            placeholder={MESSAGES.placeholders.description} 
            rows={6} 
            value={description} 
            onChange={handleDescriptionChange} 
            style={{
              width: '100%',
              padding: 16,
              border: '2px solid #e2e8f0',
              borderRadius: 12,
              fontSize: 14,
              fontFamily: 'inherit',
              resize: 'vertical'
            }} 
          />
          <div style={{color:'#718096', fontSize:13, marginTop:8}}>
            <div>• Harus diisi: detail deskripsi mengenai spot lokasi</div>
            <div style={{color:'#4299e1', marginTop:4}}>{MESSAGES.tips.description}</div>
          </div>
        </li>

        {(nfcData?.code || locationCode) && (
          <li>
            <div style={{backgroundColor:'#f0f9ff', padding:20, borderRadius:12, border:'2px solid #e0f2fe'}}>
              <h4 style={{ margin: '0 0 12px 0', color: '#0369a1' }}>📍 {MESSAGES.labels.nfcInfo}</h4>
              <div style={{fontSize:13, color:'#475569'}}>
                <div><strong>Lokasi:</strong> {spotName || nfcData?.name}</div>
                <div><strong>Kode:</strong> {nfcData?.code || locationCode}</div>
                {nfcData?.serialNumber && <div><strong>Serial:</strong> {nfcData.serialNumber}</div>}
                <div style={{marginTop:8,fontStyle:'italic',color:'#64748b',padding:'8px 0',borderTop:'1px solid #e2e8f0'}}>
                  Di-Scan pada: <components.LiveDate />
                </div>
              </div>
            </div>
          </li>
        )}
      </ul>
    </section>
  );

  const renderContent = () => articlesResult.articlesLoading ? null : (
    <main className='scrollable container'>
      {renderNFCLocationInfo()}
      {renderSuccessIndicator()}
      {renderForm()}
    </main>
  );

  const renderLoader = () => articlesResult.articlesLoading ? <components.Loader /> : null;

  const renderButton = () => {
    const isDisabled = submitting || !description.trim() || files.length === 0;
    return (
      <section style={{padding:20,zIndex:1,background:'linear-gradient(transparent, #fff 20%)',position:'sticky',bottom:0}}>
        <button 
          onClick={handleSubmit} 
          disabled={isDisabled}
          style={{
            width:'100%', 
            padding:16, 
            backgroundColor: isDisabled ? '#cbd5e0' : '#22c55e', 
            color:'white',
            border:'none', 
            borderRadius:12, 
            fontSize:16, 
            fontWeight:600, 
            cursor: isDisabled ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            if (!isDisabled) {
              e.currentTarget.style.backgroundColor = '#16a34a';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }
          }}
          onMouseLeave={(e) => {
            if (!isDisabled) {
              e.currentTarget.style.backgroundColor = '#22c55e';
              e.currentTarget.style.transform = 'translateY(0)';
            }
          }}
        >
          {submitting ? ' Mengirim..' : ` ${MESSAGES.labels.submitReport}`}
        </button>
        {submitting && (
          <div style={{textAlign:'center',marginTop:12}}>
            <small style={{color:'#64748b', fontSize:13}}>⏳ {MESSAGES.labels.submitting}</small>
          </div>
        )}
      </section>
    );
  };

  return (
    <div id='screen' style={{opacity,fontFamily:"'Inter', 'SF Pro Text', sans-serif"}}>
      {renderHeader()}
      {renderContent()}
      {renderButton()}
      {renderLoader()}
      
      <CameraComponent 
        open={cameraOpen} 
        onClose={() => setCameraOpen(false)} 
        onSave={handleCameraSave} 
      />
      
      <FileSizePopup
        isVisible={showFileSizePopup}
        onClose={() => setShowFileSizePopup(false)}
        title={MESSAGES.validation.fileTooLargeTitle}
        message="Gambar yang diambil file nya terlalu besar melebihi 4Mb"
      />
    </div>
  );
};